import React, { ReactElement, ReactNode, useEffect, useRef } from "react";

import { Box, Card, CardBody, CardFooter, Flex } from "@chakra-ui/react";

import { Entity } from "@/jenesys-types/data/common";

import { generateTestId } from "@/util/testIds";
import { performanceMonitor } from "@/utils/performance";

import {
  CrudListContextProvider,
  CrudListContextProviderProps
} from "./CrudListContext";
import { CrudListFooter } from "./CrudListFooter";
import { CrudListHeader } from "./CrudListHeader";
import { CrudListTable, CrudListTableProps } from "./CrudListTable";

export type DefaultCrudListProps<E extends Entity> =
  CrudListContextProviderProps<E> &
    CrudListTableProps<E> & { children?: never };

export type CustomCrudListProps<E extends Entity> =
  CrudListContextProviderProps<E> & {
    children: ReactNode;
    onRowItemClick?: CrudListTableProps<E>["onRowItemClick"];
  };

export type CrudListProps<E extends Entity> =
  | DefaultCrudListProps<E>
  | CustomCrudListProps<E>;

const isRenderingDefaultCrudList = <E extends Entity>(
  remainingProps: Omit<CrudListProps<E>, keyof CrudListContextProviderProps<E>>
): remainingProps is Omit<
  DefaultCrudListProps<E>,
  keyof CrudListContextProviderProps<E>
> => React.Children.count(remainingProps.children) === 0;

export function CrudList<E extends Entity = Entity>({
  actionName,
  createRouteLabel,
  createRouteName,
  createRouteParams,
  dataType,
  detailsRouteName,
  editRouteName,
  filterDefinitions,
  initialFilterCriteria,
  initialSearchCriteria,
  initialSortCriteria,
  isItemDeletable,
  isItemEditable,
  listId,
  loadOnMount = true,
  onDelete,
  onRowItemClick,
  params,
  pathParams,
  relationships,
  searchFields,
  searchOperator,
  useSimpleSearch = false,
  baselineSorts,
  localStorageConfig,
  ...rest
}: CrudListProps<E>): ReactElement<any, any> | null {
  // --- Performance Monitoring ---
  // Get loading and data state from context to monitor perceived performance
  // This assumes useCrudListContext() exposes relevant state like isLoading, currentPageEntities etc.
  // We need to read the actual context values inside the component that consumes it.
  // For CrudList, the relevant context is CrudListContextProvider which wraps its children.
  // The actual consumer of context values like isLoading, entities will be CrudListTable or custom children.
  // So, direct measurement here might be less effective than measuring inside CrudListTable or where data is displayed.
  // However, we can measure the overall render time of CrudList and time until its children are rendered.

  const componentInstanceId = useRef(
    `${listId}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
  ).current;
  const initialLoadMetricId = useRef<string | null>(null);

  // For CrudList itself, we are mainly interested in the initial setup and when its main structure is ready.
  useEffect(() => {
    const initialContext = { componentInstanceId, listId };
    initialLoadMetricId.current = performanceMonitor.start(
      "Component.CrudList.initialRender",
      initialContext
    );
    return () => {
      if (initialLoadMetricId.current) {
        performanceMonitor.stop(initialLoadMetricId.current, {
          unmount: true,
          listId
        });
        initialLoadMetricId.current = null;
      }
    };
  }, [listId, componentInstanceId]);

  // This effect will run after every render, including the initial one.
  useEffect(() => {
    if (initialLoadMetricId.current) {
      performanceMonitor.stop(initialLoadMetricId.current, {
        listId,
        status: "complete"
      });
      initialLoadMetricId.current = null;
    }
  });

  // If CrudList had its own loading state or received entities directly, we could do:
  // const { isLoading, currentPageEntities } = useCrudListContext(); // Example of getting context
  // useEffect(() => {
  //   if (isLoading) {
  //     if (!dataUpdateMark.current) { // Avoid restarting if already loading
  //        dataUpdateMark.current = performanceMonitor.start("Component.CrudList.dataLoading", componentInstanceId);
  //     }
  //   } else {
  //     if (dataUpdateMark.current) {
  //       performanceMonitor.stop(dataUpdateMark.current, "Component.CrudList.dataLoading.complete", { listId, itemCount: currentPageEntities?.length || 0 });
  //       dataUpdateMark.current = null;
  //     }
  //   }
  // }, [isLoading, currentPageEntities, listId, componentInstanceId]);

  // --- End Performance Monitoring ---

  return (
    <CrudListContextProvider
      actionName={actionName}
      createRouteLabel={createRouteLabel}
      createRouteName={createRouteName}
      createRouteParams={createRouteParams}
      dataType={dataType}
      detailsRouteName={detailsRouteName}
      editRouteName={editRouteName}
      filterDefinitions={filterDefinitions}
      initialFilterCriteria={initialFilterCriteria}
      initialSearchCriteria={initialSearchCriteria}
      initialSortCriteria={initialSortCriteria}
      isItemDeletable={isItemDeletable}
      isItemEditable={isItemEditable}
      listId={listId}
      loadOnMount={loadOnMount}
      onDelete={onDelete}
      params={params}
      pathParams={pathParams}
      relationships={relationships}
      searchFields={searchFields}
      searchOperator={searchOperator}
      useSimpleSearch={useSimpleSearch}
      baselineSorts={baselineSorts}
      localStorageConfig={localStorageConfig}
    >
      <Flex
        alignItems="stretch"
        flexFlow="column nowrap"
        justifyContent="flex-start"
        width="100%"
        data-testid={generateTestId.section("crud-list", dataType)}
      >
        {isRenderingDefaultCrudList(rest) ? (
          <Box
            marginLeft={12}
            marginRight={12}
            data-testid={generateTestId.section(
              "crud-list-container",
              dataType
            )}
          >
            <CrudListHeader />
            <Card
              marginTop={4}
              data-testid={generateTestId.section("crud-list-card", dataType)}
            >
              <CardBody
                data-testid={generateTestId.section("crud-list-body", dataType)}
              >
                <CrudListTable {...rest} onRowItemClick={onRowItemClick} />
              </CardBody>
              <CardFooter
                data-testid={generateTestId.section(
                  "crud-list-footer",
                  dataType
                )}
              >
                <CrudListFooter />
              </CardFooter>
            </Card>
          </Box>
        ) : (
          rest.children
        )}
      </Flex>
    </CrudListContextProvider>
  );
}
